import { isTuningCommand, TrialOperation, TuningTrialError } from '@playcastdotio/PeerCore';
import type { IFreshStreamTrials, IStrategyRegistry, ITuningClock, ITrialInputBinding, ITrialReplacementEndpoint,
  ResolvedTuningConfiguration, TuningCapabilities, TuningCommand, TuningIdentity, TuningPatch, TuningReceipt, TuningTrialSnapshot } from '@playcastdotio/PeerCore';
import type { TrialReplacementLease } from './TrialReplacementLease';
import type { TrialStateReset } from './TrialStateReset';

export type FreshStreamTrialManagerOptions = {
  clock: ITuningClock; registry: IStrategyRegistry; id: () => string;
  current: () => TuningIdentity; production: () => ResolvedTuningConfiguration;
  accepted: () => ResolvedTuningConfiguration; capabilities: () => readonly TuningCapabilities[];
  authorize: (command: TuningCommand) => boolean; terminal: () => boolean;
  host: ITrialReplacementEndpoint; guest: ITrialReplacementEndpoint;
  input: ITrialInputBinding; lease: TrialReplacementLease; reset: TrialStateReset;
  publish: (snapshot: TuningTrialSnapshot) => void;
  observe: (target: TuningIdentity, command: TuningCommand, configuration: ResolvedTuningConfiguration) =>
    Promise<{ effective: TuningPatch; receipts: readonly TuningReceipt[] }>;
  releaseOverrides: () => Promise<void>;
};
type SavedTrial = { intended: ResolvedTuningConfiguration; rollback: ResolvedTuningConfiguration; workloadId: string };
type ActiveTrial = { command: TuningCommand; target: TuningIdentity; saved: SavedTrial; operation: TrialOperation;
  snapshot: TuningTrialSnapshot; mutated: boolean; restoring: boolean; cleanupReason?: 'cancel' | 'stop' };
type CachedCommand = { content: string; result: Promise<TuningTrialSnapshot> };
type RestoreCause = NonNullable<TuningTrialSnapshot['restoration']>['cause'];

/** One session owns one replacement and at most one separately budgeted restoration. */
export class FreshStreamTrialManager implements IFreshStreamTrials {
  private active: ActiveTrial | undefined;
  private readonly history = new Map<string, SavedTrial>();
  private readonly commands = new Map<string, CachedCommand>();
  private issuedEpoch = 0;
  private lastCommand: TuningCommand | undefined;
  private cleanup: Promise<void> | undefined;
  private lastConnected: ResolvedTuningConfiguration | undefined;

  constructor(private readonly options: FreshStreamTrialManagerOptions) {}

  execute(command: TuningCommand): Promise<TuningTrialSnapshot> {
    if (!isTuningCommand(command) || !this.options.authorize(command) || this.options.terminal()) {
      return Promise.reject(new TuningTrialError('unauthorized', 'Fresh trial is not authorized'));
    }
    if (!['start-fresh', 'retry', 'restore'].includes(command.action.kind)) {
      return Promise.reject(new TuningTrialError('invalid-action', 'This action does not create a fresh stream'));
    }
    const content = this.commandContent(command), cached = this.commands.get(command.commandId);
    if (cached) return cached.content === content ? cached.result : Promise.reject(new TuningTrialError('command-conflict', 'Command content changed'));
    if (this.active || this.cleanup) return Promise.reject(new TuningTrialError('operation-in-progress', 'A session mutation is already active'));
    const pinned = this.clone(command);
    this.lastCommand = pinned;
    this.lastConnected ??= this.clone(this.options.accepted());
    const result = this.executeFresh(pinned);
    this.commands.set(command.commandId, { content, result });
    this.trim(this.commands, 64);
    return result;
  }

  cancel(reason: string): Promise<void> { return this.requestCleanup('cancel', reason); }
  stop(reason: string): Promise<void> { return this.requestCleanup('stop', reason); }

  private async executeFresh(command: TuningCommand): Promise<TuningTrialSnapshot> {
    const operation = new TrialOperation(this.options.clock);
    // Reserve ownership before asynchronous configuration expansion.
    const trial = this.create(command, this.options.accepted(), this.options.accepted(), 'manual', operation);
    this.active = trial;
    try {
      trial.saved = await operation.within(() => this.resolve(command), 'strategy resolution', 2_000);
      trial.snapshot = this.snapshot(trial.target, trial.saved, 'preparing');
      this.history.set(trial.target.trialId, this.clone(trial.saved)); this.trim(this.history, 128);
      this.options.lease.begin(trial.target);
      return await this.replace(trial);
    } catch (error) { return await this.failed(trial, error); }
    finally { if (this.active === trial) this.active = undefined; operation.cancel('completed'); }
  }

  private async resolve(command: TuningCommand): Promise<SavedTrial> {
    const rollback = this.clone(this.options.accepted());
    if (command.action.kind === 'start-fresh') {
      const intended = await this.options.registry.resolve(this.options.production(), command.action.strategyId,
        command.action.patch, this.options.capabilities());
      return { intended: this.clone(intended), rollback, workloadId: command.action.workloadId };
    }
    if (command.action.kind !== 'retry' && command.action.kind !== 'restore') throw new TuningTrialError('invalid-action', 'Fresh action required');
    const previous = this.history.get(command.action.fromTrialId);
    if (!previous) throw new TuningTrialError('unknown-trial', 'The requested trial is unavailable');
    return { intended: this.clone(command.action.kind === 'retry' ? previous.intended : previous.rollback), rollback,
      workloadId: previous.workloadId };
  }

  private async replace(trial: ActiveTrial): Promise<TuningTrialSnapshot> {
    const { command, target, saved, operation } = trial;
    this.emit(trial, 'preparing');
    await operation.within(() => Promise.all([this.options.host, this.options.guest].map((endpoint) =>
      endpoint.prepare(command, target, saved.intended, operation.deadlineMs))), 'both preparation acknowledgments',
      operation.startedAtMs + 2_000 - this.options.clock.monotonicMs());
    this.assertCurrent(trial);
    this.options.input.suspend(target);
    const neutral = await operation.within(() => this.options.input.neutralize(target), 'host input neutralization');
    if (neutral.delivery === 'unavailable') throw new TuningTrialError('input-neutralization-unavailable', 'Held input cannot be released safely');
    trial.mutated = true;
    await operation.within(() => this.options.reset.reset(target, saved.intended), 'isolated trial reset');
    this.emit(trial, 'replacing');
    await operation.within(() => this.options.host.replace(command, target, saved.intended), 'host replacement readiness');
    this.assertCurrent(trial);
    await operation.within(() => this.options.guest.replace(command, target, saved.intended), 'guest replacement commit');
    this.emit(trial, 'connecting');
    const evidence = await operation.within(() => this.options.guest.firstFrame(target), 'first composition acknowledgment', 10_000);
    this.assertCurrent(trial);
    const firstFrameMs = evidence.hostAckReceivedMonotonicMs - operation.startedAtMs;
    const guestFirstFrameMs = evidence.guestCompositionMonotonicMs - evidence.guestConnectionStartMonotonicMs;
    if (!Number.isFinite(firstFrameMs) || firstFrameMs < 0 || firstFrameMs > 15_000 || !Number.isFinite(guestFirstFrameMs)
      || guestFirstFrameMs < 0 || !Number.isInteger(evidence.guestEndpointConnectionEpoch) || evidence.guestEndpointConnectionEpoch < 0) {
      throw new TuningTrialError('invalid-first-frame', 'Fresh composition evidence is unavailable or stale');
    }
    const observed = await operation.within(() => this.options.observe(target, command, saved.intended), 'fresh setting readback');
    this.assertCurrent(trial);
    trial.snapshot = { ...trial.snapshot, firstFrameMs, guestFirstFrameMs, ...observed };
    this.lastConnected = this.clone(saved.intended);
    this.options.input.resume(target); this.options.lease.complete(target, 'connected');
    return this.emit(trial, 'settling');
  }

  private async failed(trial: ActiveTrial, error: unknown): Promise<TuningTrialSnapshot> {
    trial.operation.cancel('failed');
    trial.snapshot = { ...trial.snapshot, error: this.error(error) };
    this.emit(trial, trial.cleanupReason ? 'stopped' : 'failed');
    if (this.options.terminal()) { this.options.lease.complete(trial.target, 'terminal'); return trial.snapshot; }
    const cause = trial.cleanupReason ?? 'replacement-failure';
    if ((trial.mutated || trial.cleanupReason === 'stop') && !trial.restoring) {
      await this.restore(trial, trial.cleanupReason === 'stop' ? this.options.production() : trial.saved.rollback, cause);
    } else {
      const cleanup = new TrialOperation(this.options.clock, 2_000);
      await cleanup.within(() => this.abort(trial, 'trial failed'), 'prepared trial abort').catch(() => undefined);
      cleanup.cancel('aborted');
      this.options.input.resume(trial.target); this.options.lease.complete(trial.target, 'failed');
    }
    return this.clone(trial.snapshot);
  }

  private async restore(failed: ActiveTrial, configuration: ResolvedTuningConfiguration, cause: RestoreCause): Promise<void> {
    const operation = new TrialOperation(this.options.clock);
    const restored = this.create(failed.command, configuration, configuration, failed.saved.workloadId, operation);
    restored.restoring = true; this.active = restored;
    failed.snapshot = { ...failed.snapshot, restoration: { cause, trialId: restored.target.trialId, status: 'pending' } };
    this.options.publish(this.clone(failed.snapshot)); this.options.lease.extendForRestore(restored.target);
    this.history.set(restored.target.trialId, this.clone(restored.saved)); this.trim(this.history, 128);
    try {
      await operation.within(() => this.abort(failed, 'restore previous configuration'), 'failed connection abort', 2_000);
      await this.replace(restored);
      failed.snapshot = { ...failed.snapshot, restoration: { cause, trialId: restored.target.trialId, status: 'completed' } };
    } catch (error) {
      restored.snapshot = { ...restored.snapshot, error: this.error(error) }; this.emit(restored, 'failed');
      void this.abort(restored, 'restore failed').catch(() => undefined); this.options.lease.complete(restored.target, 'failed');
      failed.snapshot = { ...failed.snapshot, restoration: { cause, trialId: restored.target.trialId, status: 'failed' } };
    } finally {
      operation.cancel('restoration complete'); this.active = failed; this.options.publish(this.clone(failed.snapshot));
    }
  }

  private requestCleanup(cause: 'cancel' | 'stop', reason: string): Promise<void> {
    if (this.cleanup) return this.cleanup;
    const active = this.active;
    if (cause === 'cancel' && !active) return Promise.resolve();
    if (cause === 'cancel' && active && !this.options.authorize(active.command)) return Promise.reject(new TuningTrialError('unauthorized', 'Cancel requires an active lease'));
    if (active) { active.cleanupReason = cause; active.operation.cancel(reason); }
    this.cleanup = this.performCleanup(active, cause, reason).finally(() => { this.cleanup = undefined; });
    return this.cleanup;
  }

  private async performCleanup(active: ActiveTrial | undefined, cause: 'cancel' | 'stop', reason: string): Promise<void> {
    let outcome: TuningTrialSnapshot | undefined;
    if (active) {
      outcome = await this.commands.get(active.command.commandId)?.result;
    }
    if (cause === 'stop' && this.lastCommand && !this.options.terminal()
      && this.lastConnected?.configurationHash !== this.options.production().configurationHash && outcome?.restoration?.cause !== 'stop') {
      const operation = new TrialOperation(this.options.clock);
      const previous = this.create(this.lastCommand, this.options.accepted(), this.options.accepted(), 'stop', operation);
      await this.restore(previous, this.options.production(), 'stop'); outcome = previous.snapshot; operation.cancel(reason); this.active = undefined;
    }
    if (cause === 'stop') await this.options.releaseOverrides();
    if (this.options.terminal() && active) this.options.lease.complete(active.target, 'terminal');
    if (outcome?.restoration?.status === 'failed') throw new TuningTrialError('restoration-failed', 'Fresh-stream cleanup failed within its bounded attempt');
  }

  private create(command: TuningCommand, intended: ResolvedTuningConfiguration, rollback: ResolvedTuningConfiguration,
    workloadId: string, operation: TrialOperation): ActiveTrial {
    const current = this.options.current();
    const target = { ...current, trialId: this.options.id(), connectionEpoch: this.issuedEpoch = Math.max(this.issuedEpoch, current.connectionEpoch) + 1 };
    const saved = { intended: this.clone(intended), rollback: this.clone(rollback), workloadId };
    return { command, target, saved, operation, snapshot: this.snapshot(target, saved, 'preparing'), mutated: false, restoring: false };
  }

  private snapshot(target: TuningIdentity, saved: SavedTrial, phase: TuningTrialSnapshot['phase']): TuningTrialSnapshot {
    return { target, phase, requested: saved.intended.settings, effective: {}, configurationHash: saved.intended.configurationHash,
      strategyId: saved.intended.strategyId, workloadId: saved.workloadId, receipts: [], latest: [] };
  }
  private emit(trial: ActiveTrial, phase: TuningTrialSnapshot['phase']): TuningTrialSnapshot {
    trial.snapshot = { ...trial.snapshot, phase }; this.options.publish(this.clone(trial.snapshot)); return this.clone(trial.snapshot);
  }
  private assertCurrent(trial: ActiveTrial): void {
    trial.operation.assertActive();
    if (this.active !== trial || this.options.terminal() || (!trial.restoring && !this.options.authorize(trial.command))) {
      throw new TuningTrialError('superseded', 'Trial authority or ownership changed');
    }
  }
  private abort(trial: ActiveTrial, reason: string): Promise<void> {
    return Promise.all([this.options.host, this.options.guest].map((endpoint) => endpoint.abort(trial.target, reason))).then(() => undefined);
  }
  private error(error: unknown): { code: string; message: string } {
    return { code: error instanceof TuningTrialError ? error.code : 'replacement-failed', message: error instanceof Error ? error.message : 'Replacement failed' };
  }
  private clone<T>(value: T): T { return JSON.parse(JSON.stringify(value)) as T; }
  private trim<K, V>(map: Map<K, V>, limit: number): void { if (map.size > limit) map.delete(map.keys().next().value as K); }
  private commandContent(command: TuningCommand): string {
    const sorted = (value: unknown): unknown => !value || typeof value !== 'object' || Array.isArray(value) ? value
      : Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, nested]) => [key, sorted(nested)]));
    return JSON.stringify(sorted(command));
  }
}
